// @ts-nocheck
import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Shield, Calculator, Home, Wallet, Building, Download, CheckCircle, TreePine } from 'lucide-react';
import { getNames } from 'country-list';
import './App.css';

interface BilanPatrimonialProps {
  onNavigateToSOeasi: () => void;
  userProfileData: any;
}

const BilanPatrimonial: React.FC<BilanPatrimonialProps> = ({ onNavigateToSOeasi, userProfileData }) => {
  const [activeTab, setActiveTab] = useState('infos-perso');
  
  // Liste des pays
  const countries = getNames().sort();
  
  // États pour la simulation SOeasi
  const [simulationParams, setSimulationParams] = useState({
    monthlyAmount: 300,
    duration: 8,
    investmentType: 'monthly',
    lumpSum: 10000,
    withRegularPayments: true
  });
  const simulationSaved = false;
  const [additionalActivities, setAdditionalActivities] = useState([]);
  const [customAllocation, setCustomAllocation] = useState(null);
  const [isEditingAllocation, setIsEditingAllocation] = useState(false);

  // Fonction pour ajouter une nouvelle activité
  const addNewActivity = () => {
    const newActivity = {
      id: Date.now(),
      status: 'Salarié',
      shares: 0,
      equipment: 0,
      business: 0
    };
    setAdditionalActivities([...additionalActivities, newActivity]);
  };

  // Fonction pour supprimer une activité
  const removeActivity = (id) => {
    setAdditionalActivities(additionalActivities.filter(activity => activity.id !== id));
  };

  // Fonction pour gérer l'allocation personnalisée
  const handleAllocationChange = (product, value) => {
    const newAllocation = { ...customAllocation };
    newAllocation[product] = Math.max(0, Math.min(100, value));
    
    // Recalculer pour que le total soit 100%
    const total = Object.values(newAllocation).reduce((sum, val) => sum + val, 0);
    if (total > 100) {
      // Réduire proportionnellement les autres produits
      const excess = total - 100;
      const otherProducts = Object.keys(newAllocation).filter(p => p !== product);
      const otherTotal = otherProducts.reduce((sum, p) => sum + newAllocation[p], 0);
      
      if (otherTotal > 0) {
        otherProducts.forEach(p => {
          const reduction = (newAllocation[p] / otherTotal) * excess;
          newAllocation[p] = Math.max(0, newAllocation[p] - reduction);
        });
      }
    }
    
    setCustomAllocation(newAllocation);
  };

  // Fonction pour réinitialiser l'allocation
  const resetAllocation = () => {
    setCustomAllocation(null);
    setIsEditingAllocation(false);
  };

  // Fonction pour commencer l'édition
  const startEditingAllocation = () => {
    const recommended = getRecommendedAllocation(userProfile.riskScore);
    setCustomAllocation({ ...recommended });
    setIsEditingAllocation(true);
  };


  // Fonction pour générer et télécharger le PDF
  const downloadPDF = async () => {
    try {
      // Créer l'élément et l'ajouter temporairement au DOM
      const element = document.createElement('div');
      element.style.position = 'absolute';
      element.style.left = '-9999px';
      element.style.top = '-9999px';
      element.style.width = '800px';
      element.innerHTML = `
        <div style="
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
          background: linear-gradient(135deg, #f8fdfc 0%, #e6fffa 100%);
          padding: 0;
          margin: 0;
          min-height: 100vh;
        ">
          <!-- Header avec gradient -->
          <div style="
            background: linear-gradient(135deg, #00BCAA 0%, #0082BC 100%);
            padding: 40px 30px;
            text-align: center;
            color: white;
            margin-bottom: 0;
          ">
            <h1 style="
              margin: 0;
              font-size: 32px;
              font-weight: 700;
              text-shadow: 0 2px 4px rgba(0,0,0,0.1);
              letter-spacing: 1px;
            ">📊 BILAN PATRIMONIAL</h1>
            <p style="
              margin: 10px 0 0 0;
              font-size: 16px;
              opacity: 0.9;
              font-weight: 300;
            ">Analyse personnalisée de votre profil d'investissement</p>
          </div>

          <div style="padding: 30px;">
            <!-- Profil Investisseur -->
            <div style="
              background: white;
              border-radius: 15px;
              padding: 25px;
              margin-bottom: 25px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
              border-left: 5px solid #00BCAA;
            ">
              <h2 style="
                color: #00BCAA;
                margin: 0 0 20px 0;
                font-size: 22px;
                font-weight: 600;
                display: flex;
                align-items: center;
              ">Profil Investisseur</h2>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px;">
                <div style="
                  background: linear-gradient(135deg, #C1E0E0 0%, #BBE3F2 100%);
                  padding: 15px;
                  border-radius: 10px;
                  text-align: center;
                ">
                  <div style="font-size: 24px; font-weight: 700; color: #0B696B; margin-bottom: 5px;">
                    ${userProfile.riskScore}/10
                  </div>
                  <div style="font-size: 14px; color: #1A3F5B; font-weight: 500;">Score de risque</div>
                </div>
                
                <div style="
                  background: linear-gradient(135deg, #BBE3F2 0%, #C1E0E0 100%);
                  padding: 15px;
                  border-radius: 10px;
                  text-align: center;
                ">
                  <div style="font-size: 18px; font-weight: 700; color: #1A3F5B; margin-bottom: 5px;">
                    ${userProfile.riskScore <= 3 ? "🛡️ Conservateur" : 
                      userProfile.riskScore <= 6 ? "⚖️ Équilibré" : "🚀 Dynamique"}
                  </div>
                  <div style="font-size: 14px; color: #0B696B; font-weight: 500;">Type de profil</div>
                </div>
              </div>
            </div>

            <!-- Simulation d'Investissement -->
            <div style="
              background: white;
              border-radius: 15px;
              padding: 25px;
              margin-bottom: 25px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
              border-left: 5px solid #0082BC;
              page-break-inside: avoid;
              break-inside: avoid;
            ">
              <h2 style="
                color: #0082BC;
                margin: 0 0 20px 0;
                font-size: 22px;
                font-weight: 600;
              ">💰 Simulation d'Investissement</h2>
              
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
                <div style="background: #f8fdfc; padding: 15px; border-radius: 8px; border: 1px solid #C1E0E0;">
                  <strong style="color: #0B696B;">Type:</strong><br>
                  <span style="color: #1A3F5B; font-size: 16px;">${
                    simulationParams.investmentType === 'monthly' ? '📅 Versements mensuels' :
                    simulationParams.investmentType === 'lumpsum' ? '💎 Montant unique' : '🔄 Capital + Épargne'
                  }</span>
                </div>
                
                ${simulationParams.investmentType !== 'lumpsum' ? `
                <div style="background: #f8fdfc; padding: 15px; border-radius: 8px; border: 1px solid #C1E0E0;">
                  <strong style="color: #0B696B;">Mensuel:</strong><br>
                  <span style="color: #1A3F5B; font-size: 18px; font-weight: 600;">${simulationParams.monthlyAmount}€</span>
                </div>` : ''}
                
                ${simulationParams.investmentType !== 'monthly' ? `
                <div style="background: #f8fdfc; padding: 15px; border-radius: 8px; border: 1px solid #C1E0E0;">
                  <strong style="color: #0B696B;">Capital initial:</strong><br>
                  <span style="color: #1A3F5B; font-size: 18px; font-weight: 600;">${simulationParams.lumpSum.toLocaleString()}€</span>
                </div>` : ''}
                
                <div style="background: #f8fdfc; padding: 15px; border-radius: 8px; border: 1px solid #C1E0E0;">
                  <strong style="color: #0B696B;">Durée:</strong><br>
                  <span style="color: #1A3F5B; font-size: 18px; font-weight: 600;">${simulationParams.duration} ans</span>
                </div>
              </div>
            </div>

            <!-- Projection Financière -->
            <div style="
              background: white;
              border-radius: 15px;
              padding: 25px;
              margin-bottom: 25px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
              border-left: 5px solid #00BCAA;
              page-break-inside: avoid;
              break-inside: avoid;
            ">
              <h2 style="
                color: #00BCAA;
                margin: 0 0 20px 0;
                font-size: 22px;
                font-weight: 600;
              ">📈 Projection Financière</h2>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
                <div style="
                  background: linear-gradient(135deg, #C1E0E0 0%, #f8fdfc 100%);
                  padding: 20px;
                  border-radius: 12px;
                  text-align: center;
                ">
                  <div style="font-size: 24px; font-weight: 700; color: #0B696B; margin-bottom: 8px;">
                    ${totalInvested.toLocaleString()}€
                  </div>
                  <div style="font-size: 14px; color: #1A3F5B; font-weight: 500;">Capital total investi</div>
                </div>
                
                <div style="
                  background: linear-gradient(135deg, #BBE3F2 0%, #f8fdfc 100%);
                  padding: 20px;
                  border-radius: 12px;
                  text-align: center;
                ">
                  <div style="font-size: 24px; font-weight: 700; color: #1A3F5B; margin-bottom: 8px;">
                    ${finalValue.toLocaleString()}€
                  </div>
                  <div style="font-size: 14px; color: #0B696B; font-weight: 500;">Valeur finale estimée</div>
                </div>
              </div>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px;">
                <div style="
                  background: linear-gradient(135deg, #e6fffa 0%, #C1E0E0 100%);
                  padding: 20px;
                  border-radius: 12px;
                  text-align: center;
                ">
                  <div style="font-size: 24px; font-weight: 700; color: #00BCAA; margin-bottom: 8px;">
                    +${totalGains.toLocaleString()}€
                  </div>
                  <div style="font-size: 14px; color: #0B696B; font-weight: 500;">Plus-value estimée</div>
                </div>
                
                <div style="
                  background: linear-gradient(135deg, #f0f9ff 0%, #BBE3F2 100%);
                  padding: 20px;
                  border-radius: 12px;
                  text-align: center;
                ">
                  <div style="font-size: 24px; font-weight: 700; color: #0082BC; margin-bottom: 8px;">
                    +${((totalGains / totalInvested) * 100).toFixed(1)}%
                  </div>
                  <div style="font-size: 14px; color: #1A3F5B; font-weight: 500;">Rendement total</div>
                </div>
              </div>
            </div>

            <!-- Saut de page forcé -->
            <div style="
              page-break-after: always;
              break-after: page;
              height: 1px;
              margin: 0;
              padding: 0;
            "></div>

            <!-- Allocation Recommandée -->
            <div style="
              background: white;
              border-radius: 15px;
              padding: 25px;
              margin-bottom: 25px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
              border-left: 5px solid #0082BC;
              page-break-inside: avoid;
              break-inside: avoid;
            ">
              <h2 style="
                color: #0082BC;
                margin: 0 0 20px 0;
                font-size: 22px;
                font-weight: 600;
              ">Allocation Recommandée</h2>
              
              <div style="
                display: grid; 
                grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); 
                gap: 15px;
                page-break-inside: avoid;
                break-inside: avoid;
              ">
                ${Object.entries(optimalAllocation).map(([key, value]) => {
                  const productName = soeasi_products[key]?.name || key;
                  return `
                    <div style="
                      background: linear-gradient(135deg, #f8fdfc 0%, #e6fffa 100%);
                      padding: 18px;
                      border-radius: 10px;
                      border: 1px solid #C1E0E0;
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      page-break-inside: avoid;
                      break-inside: avoid;
                    ">
                      <span style="color: #0B696B; font-weight: 600; font-size: 15px;">${productName}</span>
                      <span style="
                        background: linear-gradient(135deg, #00BCAA 0%, #0082BC 100%);
                        color: white;
                        padding: 6px 12px;
                        border-radius: 20px;
                        font-weight: 700;
                        font-size: 14px;
                      ">${value}%</span>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>

            <!-- Footer -->
            <div style="
              background: linear-gradient(135deg, #1A3F5B 0%, #0B696B 100%);
              color: white;
              padding: 25px;
              border-radius: 15px;
              text-align: center;
              margin-top: 30px;
            ">
              <p style="margin: 0 0 10px 0; font-size: 16px; font-weight: 500;">
                📅 Document généré le ${new Date().toLocaleDateString('fr-FR')}
              </p>
              <p style="margin: 0; font-size: 14px; opacity: 0.8; font-style: italic;">
                ⚠️ Ce document est fourni à titre informatif uniquement
              </p>
            </div>
          </div>
        </div>
      `;

      // Ajouter l'élément au DOM temporairement
      document.body.appendChild(element);

      // Utiliser html2canvas et jsPDF pour générer le PDF
      const html2canvas = await import('html2canvas');
      const { jsPDF } = await import('jspdf');
      
      const canvas = await html2canvas.default(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Supprimer l'élément du DOM
      document.body.removeChild(element);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('bilan-patrimonial.pdf');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Profil utilisateur par défaut
  const [userProfile, setUserProfile] = useState({
    riskScore: 5,
    experience: 'intermediate',
    age: 35,
    objective: 'retirement'
  });

  // Produits SOeasi
  const soeasi_products = {
    assurance_vie: {
      name: "Assurance Vie Islamique",
      description: "Takaful - Protection et épargne conformes",
      return: 4.2,
      fees: 1.5,
      risk: 6,
      icon: Shield,
      color: '#10b981'
    },
    sukuk_projects: {
      name: "Projets Sukuk",
      description: "Obligations islamiques sur projets d'infrastructure",
      return: 6.8,
      fees: 1.2,
      risk: 3,
      icon: Building,
      color: '#3b82f6'
    },
    real_estate: {
      name: "Investissement Immobilier",
      description: "SCPI islamiques et investissements fonciers",
      return: 4.72,
      fees: 2.8,
      risk: 4,
      icon: Home,
      color: '#f59e0b'
    },
    retirement_savings: {
      name: "Épargne Retraite",
      description: "Plan d'épargne retraite conforme",
      return: 7.2,
      fees: 1.8,
      risk: 5,
      icon: Wallet,
      color: '#8b5cf6'
    },
    forest_funds: {
      name: "Fonds Forestier",
      description: "Investissement dans la sylviculture durable",
      return: 8.5,
      fees: 2.5,
      risk: 3,
      icon: TreePine,
      color: '#059669'
    }
  };



  // Calcul de l'allocation optimale basée sur le profil (scores de risque inversés)
  const getRecommendedAllocation = (riskScore) => {
    const allocations = {
      1: { assurance_vie: 5, sukuk_projects: 45, real_estate: 15, retirement_savings: 30, forest_funds: 5 },
      2: { assurance_vie: 10, sukuk_projects: 40, real_estate: 20, retirement_savings: 25, forest_funds: 5 },
      3: { assurance_vie: 15, sukuk_projects: 35, real_estate: 25, retirement_savings: 20, forest_funds: 5 },
      4: { assurance_vie: 20, sukuk_projects: 30, real_estate: 25, retirement_savings: 20, forest_funds: 5 },
      5: { assurance_vie: 25, sukuk_projects: 25, real_estate: 25, retirement_savings: 20, forest_funds: 5 },
      6: { assurance_vie: 30, sukuk_projects: 20, real_estate: 25, retirement_savings: 20, forest_funds: 5 },
      7: { assurance_vie: 40, sukuk_projects: 15, real_estate: 25, retirement_savings: 15, forest_funds: 5 },
      8: { assurance_vie: 50, sukuk_projects: 10, real_estate: 25, retirement_savings: 10, forest_funds: 5 }
    };
    return allocations[Math.min(8, Math.max(1, riskScore))] || allocations[5];
  };

  // Calcul des projections
  const runProjection = (allocation, params) => {
    const projections = [];
    let totalValue = params.investmentType === 'lumpsum' || params.investmentType === 'mixed' ? params.lumpSum : 0;
    let totalInvested = params.investmentType === 'lumpsum' || params.investmentType === 'mixed' ? params.lumpSum : 0;
    
    for (let year = 1; year <= params.duration; year++) {
      if (params.investmentType === 'monthly' || (params.investmentType === 'mixed' && params.withRegularPayments)) {
        totalInvested += params.monthlyAmount * 12;
      }
      
      let weightedReturn = 0;
      Object.entries(allocation).forEach(([product, weight]) => {
        if (weight > 0 && soeasi_products[product]) {
          const productReturn = soeasi_products[product].return / 100;
          const productFees = soeasi_products[product].fees / 100;
          weightedReturn += (weight / 100) * (productReturn - productFees);
        }
      });
      
      totalValue = totalValue * (1 + weightedReturn);
      
      if (params.investmentType === 'monthly' || (params.investmentType === 'mixed' && params.withRegularPayments)) {
        totalValue += params.monthlyAmount * 12;
      }
      
      projections.push({
        year,
        invested: totalInvested,
        value: Math.round(totalValue),
        gains: Math.round(totalValue - totalInvested),
        yearlyGain: year === 1 ? Math.round(totalValue - totalInvested) : 
                   Math.round(totalValue - projections[year-2].value - (params.investmentType === 'monthly' || (params.investmentType === 'mixed' && params.withRegularPayments) ? params.monthlyAmount * 12 : 0))
      });
    }
    
    return projections;
  };

  // Variables calculées avec données du simulateur SOeasi
  const userRiskScore = userProfileData?.riskProfile || 5;
  const userExperience = userProfileData?.experience || 'intermediate';
  const userObjective = userProfileData?.objective || 'wealth';
  const userIncome = userProfileData?.income || 3500;
  
  const recommendedAllocation = getRecommendedAllocation(userRiskScore);
  const optimalAllocation = customAllocation || recommendedAllocation;
  const projections = runProjection(optimalAllocation, simulationParams);

  const lastProjection = projections[projections.length - 1];
  const totalInvested = lastProjection?.invested || 0;
  const finalValue = lastProjection?.value || 0;
  const totalGains = finalValue - totalInvested;


  return (
    <div className="bilan-main-container">
      {/* Top Right Return Button */}
      <button
        onClick={onNavigateToSOeasi}
        className="bilan-return-button"
      >
        ← Retour SOeasi
      </button>
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-content">
          <div className="sidebar-header">
            <div className="bilan-sidebar-header-badge">
              Bilan Patrimonial
            </div>
          </div>
          
          <nav className="bilan-nav-container">
            <button
              onClick={() => setActiveTab('infos-perso')}
              className={`bilan-nav-button ${activeTab === 'infos-perso' ? 'active' : ''}`}
            >
              <span className="nav-button-number">01</span>
              Infos Perso.
            </button>
            
            <button
              onClick={() => setActiveTab('profil-investisseur')}
              className={`bilan-nav-button ${activeTab === 'profil-investisseur' ? 'active' : ''}`}
            >
              <span className="nav-button-number">02</span>
              Profil Investisseur
            </button>
            
            <button
              onClick={() => setActiveTab('patrimoine-immo')}
              className={`bilan-nav-button ${activeTab === 'patrimoine-immo' ? 'active' : ''}`}
            >
              <span className="nav-button-number">03</span>
              Patrimoine Immo. & Fin.
            </button>
            
            <button
              onClick={() => setActiveTab('actifs-pro')}
              className={`bilan-nav-button ${activeTab === 'actifs-pro' ? 'active' : ''}`}
            >
              <span className="nav-button-number">04</span>
              Actifs Pro.
            </button>
            
            <button
              onClick={() => setActiveTab('dettes-credits')}
              className={`bilan-nav-button ${activeTab === 'dettes-credits' ? 'active' : ''}`}
            >
              <span className="nav-button-number">05</span>
              Dettes & Crédits
            </button>
            
            <button
              onClick={() => setActiveTab('revenus-depenses')}
              className={`bilan-nav-button ${activeTab === 'revenus-depenses' ? 'active' : ''}`}
            >
              <span className="nav-button-number">06</span>
              Revenus & Dépenses
            </button>
            
            <button
              onClick={() => setActiveTab('objectifs-projections')}
              className={`bilan-nav-button ${activeTab === 'objectifs-projections' ? 'active' : ''}`}
            >
              <span className="nav-button-number">07</span>
              Objectifs & Projections
            </button>
            
            <button
              onClick={() => setActiveTab('synthese-commentaires')}
              className={`bilan-nav-button ${activeTab === 'synthese-commentaires' ? 'active' : ''}`}
            >
              <span className="nav-button-number">08</span>
              Synthèse & Commentaires
            </button>
            
            <button
              onClick={() => setActiveTab('optimisation-fiscale')}
              className={`bilan-nav-button ${activeTab === 'optimisation-fiscale' ? 'active' : ''}`}
            >
              <span className="nav-button-number">09</span>
              Optimisation Fiscale
            </button>
            
            <button
              onClick={() => setActiveTab('bilan-pdf')}
              className={`bilan-nav-button ${activeTab === 'bilan-pdf' ? 'active' : ''}`}
            >
              <span className="nav-button-number">10</span>
              Mon Bilan PDF
            </button>
            
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="bilan-main-content">
        <div className="bilan-content-wrapper">
          <div className="bilan-content-section">
            <h2 className="bilan-tab-header">
              {activeTab === 'infos-perso' && 'Informations Personnelles'}
              {activeTab === 'profil-investisseur' && 'Profil Investisseur'}
              {activeTab === 'patrimoine-immo' && 'Patrimoine Immobilier & Financier'}
              {activeTab === 'actifs-pro' && 'Actifs Professionnels'}
              {activeTab === 'dettes-credits' && 'Dettes & Crédits'}
              {activeTab === 'revenus-depenses' && 'Revenus & Dépenses'}
              {activeTab === 'objectifs-projections' && 'Objectifs & Projections'}
              {activeTab === 'synthese-commentaires' && 'Synthèse & Commentaires'}
              {activeTab === 'optimisation-fiscale' && ''}
              {activeTab === 'bilan-pdf' && 'Mon Bilan PDF'}
            </h2>
            <p className="bilan-tab-description">
              Analysez votre situation financière et optimisez vos investissements
            </p>
          </div>

          {/* Section Content */}
          {activeTab === 'infos-perso' && (
            <div className="bilan-simulation-container">
              <div className="bilan-info-card">
                <h3 className="bilan-section-title">
                  Identité
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Prénom:</label>
                    <input 
                      type="text" 
                      placeholder="Votre prénom" 
                      className="bilan-form-input"
                      defaultValue={userProfileData?.firstName || ''}
                    />
                  </div>
                  <div>
                    <label className="bilan-form-label">Nom:</label>
                    <input 
                      type="text" 
                      placeholder="Votre nom de famille" 
                      className="bilan-form-input"
                      defaultValue={userProfileData?.lastName || ''}
                    />
                  </div>
                  <div>
                    <label className="bilan-form-label">Âge:</label>
                    <input 
                      type="number" 
                      placeholder="35" 
                      className="bilan-form-input"
                      defaultValue={userProfileData?.age || 35}
                    />
                  </div>
                  <div>
                    <label className="bilan-form-label">Nationalité:</label>
                    <select className="bilan-form-select">
                      <option>Française</option>
                      <option>Algérienne</option>
                      <option>Marocaine</option>
                      <option>Tunisienne</option>
                      <option>Belge</option>
                      <option>Suisse</option>
                      <option>Canadienne</option>
                      <option>Allemande</option>
                      <option>Italienne</option>
                      <option>Espagnole</option>
                      <option>Britannique</option>
                      <option>Américaine</option>
                      <option>Turque</option>
                      <option>Égyptienne</option>
                      <option>Libanaise</option>
                      <option>Syrienne</option>
                      <option>Jordanienne</option>
                      <option>Saoudienne</option>
                      <option>Émirienne</option>
                      <option>Qatarienne</option>
                      <option>Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Situation familiale:</label>
                    <select className="bilan-form-select">
                      <option>Célibataire</option>
                      <option>Marié(e)</option>
                      <option>Divorcé(e)</option>
                      <option>Veuf(ve)</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Nombre d'enfants:</label>
                    <input type="number" defaultValue="0" min="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Profession:</label>
                    <input type="text" placeholder="Votre profession" className="bilan-form-input" />
                  </div>
                </div>
              </div>
              
              <div className="bilan-info-card">
                <h3 className="bilan-section-title">
                  Résidence
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Adresse:</label>
                    <input type="text" placeholder="Votre adresse" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Ville:</label>
                    <input type="text" placeholder="Votre ville" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Pays de résidence:</label>
                    <select className="bilan-form-select">
                      <option value="">Sélectionnez votre pays</option>
                      {countries.map((country, index) => (
                        <option key={index} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Type de logement:</label>
                    <select className="bilan-form-select">
                      <option>Appartement</option>
                      <option>Maison</option>
                      <option>Studio</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Statut:</label>
                    <select className="bilan-form-select">
                      <option>Propriétaire</option>
                      <option>Locataire</option>
                      <option>Logé gratuitement</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profil-investisseur' && (
            <div className="bilan-investor-grid">
              <div className="bilan-info-card">
                <h3 className="bilan-section-title">
                  Profil de Risque
                </h3>
                <div className="bilan-score-display">
                  <div className="bilan-score-card-inline">
                    <div className="bilan-score-content">
                      <div className="bilan-score-info">
                        <span className="bilan-score-label">Score de risque:</span>
                        <span className="bilan-score-value">{userRiskScore}/10</span>
                      </div>
                      <div className="bilan-score-type">
                        {userRiskScore <= 3 && "🛡️ Conservateur"}
                        {userRiskScore > 3 && userRiskScore <= 6 && "⚖️ Équilibré"}
                        {userRiskScore > 6 && "🚀 Dynamique"}
                      </div>
                      <div className="bilan-score-bar">
                        <div 
                          className="bilan-score-fill" 
                          style={{width: `${(userRiskScore / 10) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Score de risque (1-10):</label>
                    <input 
                      type="range" 
                      min="1" 
                      max="10" 
                      value={userProfile.riskScore} 
                      onChange={(e) => setUserProfile({...userProfile, riskScore: parseInt(e.target.value)})}
                      className="bilan-form-range" 
                    />
                  </div>
                  <div>
                    <label className="bilan-form-label">Expérience d'investissement:</label>
                    <select className="bilan-form-select" defaultValue={userExperience}>
                      <option value="beginner">Débutant</option>
                      <option value="intermediate">Intermédiaire</option>
                      <option value="advanced">Expérimenté</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Objectif d'épargne:</label>
                    <select className="bilan-form-select" defaultValue={userObjective}>
                      <option value="retirement">Préparer ma retraite</option>
                      <option value="house">Financer un projet immobilier</option>
                      <option value="emergency">Constituer une épargne de précaution</option>
                      <option value="wealth">Faire fructifier mon patrimoine</option>
                      <option value="children">Financer les études des enfants</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Revenu mensuel net:</label>
                    <select className="bilan-form-select" defaultValue={userIncome}>
                      <option value={2000}>Moins de 2500€</option>
                      <option value={3500}>2500€ - 4000€</option>
                      <option value={5000}>4000€ - 6000€</option>
                      <option value={7500}>6000€ - 8000€</option>
                      <option value={10000}>Plus de 8000€</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="bilan-info-card">
                <h3 className="bilan-section-title">
                  Préférences
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-checkbox-container">
                      <input type="checkbox" className="bilan-checkbox" />
                      <span className="bilan-checkbox-label">Investissement éthique/islamique</span>
                    </label>
                  </div>
                  <div>
                    <label className="bilan-form-label">Secteurs privilégiés:</label>
                    <select className="bilan-form-select">
                      <option>Technologie</option>
                      <option>Santé & Biotechnologies</option>
                      <option>Immobilier</option>
                      <option>Énergie & Environnement</option>
                      <option>Finance éthique</option>
                      <option>Agroalimentaire</option>
                      <option>Infrastructure</option>
                      <option>Éducation</option>
                      <option>Commerce & Distribution</option>
                      <option>Industrie manufacturière</option>
                      <option>Transport & Logistique</option>
                      <option>Télécommunications</option>
                      <option>Tourisme & Hôtellerie</option>
                      <option>Matières premières</option>
                      <option>Services financiers</option>
                      <option>Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Diversification géographique:</label>
                    <select className="bilan-form-select">
                      <option>France uniquement</option>
                      <option>Europe</option>
                      <option>Internationale</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Liquidité souhaitée:</label>
                    <select className="bilan-form-select">
                      <option>Faible</option>
                      <option>Moyenne</option>
                      <option>Élevée</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'patrimoine-immo' && (
            <div className="bilan-investor-grid">
              <div className="bilan-info-card">
                <h3 className="bilan-section-title">
                  Patrimoine Immobilier
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Résidence principale (€):</label>
                    <input type="number" placeholder="320000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Investissement locatif (€):</label>
                    <input type="number" placeholder="180000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Autres biens immobiliers (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Rendement locatif moyen (%):</label>
                    <input type="number" step="0.1" placeholder="4.2" className="bilan-form-input" />
                  </div>
                </div>
              </div>
              
              <div className="bilan-info-card">
                <h3 className="bilan-section-title">
                  Patrimoine Financier
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Comptes épargne (€):</label>
                    <input type="number" placeholder="45000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Placements financiers (€):</label>
                    <input type="number" placeholder="85000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Assurance vie (€):</label>
                    <input type="number" placeholder="120000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">PEA/PEE (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section Actifs Professionnels */}
          {activeTab === 'actifs-pro' && (
            <div className="bilan-investor-grid">
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Entreprise/Société
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Statut professionnel:</label>
                    <select className="bilan-form-select">
                      <option>Salarié</option>
                      <option>Indépendant</option>
                      <option>Chef d'entreprise</option>
                      <option>Associé</option>
                      <option>Fonctionnaire</option>
                      <option>Retraité</option>
                      <option>Étudiant</option>
                      <option>Demandeur d'emploi</option>
                      <option>Profession libérale</option>
                      <option>Consultant</option>
                      <option>Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Parts sociales (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Matériel professionnel (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Fonds de commerce (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                </div>
                
                {/* Activités supplémentaires */}
                {additionalActivities.map((activity) => (
                  <div key={activity.id} className="bilan-form-container" style={{marginTop: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                      <h4 style={{color: '#14b8a6', fontWeight: '600'}}>Autre statut professionnel</h4>
                      <button 
                        type="button" 
                        onClick={() => removeActivity(activity.id)}
                        style={{background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.2rem'}}
                      >
                        ×
                      </button>
                    </div>
                    <div>
                      <label className="bilan-form-label">Statut professionnel:</label>
                      <select className="bilan-form-select">
                        <option>Salarié</option>
                        <option>Indépendant</option>
                        <option>Chef d'entreprise</option>
                        <option>Associé</option>
                        <option>Fonctionnaire</option>
                        <option>Retraité</option>
                        <option>Étudiant</option>
                        <option>Demandeur d'emploi</option>
                        <option>Profession libérale</option>
                        <option>Consultant</option>
                        <option>Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="bilan-form-label">Parts sociales (€):</label>
                      <input type="number" placeholder="0" className="bilan-form-input" />
                    </div>
                    <div>
                      <label className="bilan-form-label">Matériel professionnel (€):</label>
                      <input type="number" placeholder="0" className="bilan-form-input" />
                    </div>
                    <div>
                      <label className="bilan-form-label">Fonds de commerce (€):</label>
                      <input type="number" placeholder="0" className="bilan-form-input" />
                    </div>
                  </div>
                ))}
                
                <div className="bilan-add-activity">
                  <button type="button" className="bilan-add-button" onClick={addNewActivity}>
                    + Ajouter une autre activité
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Section Dettes & Crédits */}
          {activeTab === 'dettes-credits' && (
            <div className="bilan-investor-grid">
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Crédits Immobiliers
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Capital restant dû (€):</label>
                    <input type="number" placeholder="150000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Mensualité (€):</label>
                    <input type="number" placeholder="1200" className="bilan-form-select" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Taux (%):</label>
                    <input type="number" step="0.01" placeholder="1.5" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Durée restante (années):</label>
                    <input type="number" placeholder="15" className="bilan-form-input" />
                  </div>
                </div>
              </div>
              
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Autres Crédits
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Crédit consommation (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Crédit auto (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Découverts bancaires (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Autres dettes (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section Revenus & Dépenses */}
          {activeTab === 'revenus-depenses' && (
            <div className="bilan-investor-grid">
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Revenus Mensuels
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Salaire net (€):</label>
                    <input type="number" placeholder="3500" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Revenus locatifs (€):</label>
                    <input type="number" placeholder="800" className="bilan-form-select" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Autres revenus (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Pensions/Allocations (€):</label>
                    <input type="number" placeholder="0" className="bilan-form-input" />
                  </div>
                </div>
              </div>
              
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Dépenses Mensuelles
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Logement (€):</label>
                    <input type="number" placeholder="1200" className="bilan-form-select" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Alimentation (€):</label>
                    <input type="number" placeholder="600" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Transport (€):</label>
                    <input type="number" placeholder="300" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Autres dépenses (€):</label>
                    <input type="number" placeholder="500" className="bilan-form-input" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section Objectifs & Projections */}
          {activeTab === 'objectifs-projections' && (
            <div className="bilan-investor-grid">
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Objectifs Financiers
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Objectif principal:</label>
                    <select className="bilan-form-select">
                      <option>Préparer la retraite</option>
                      <option>Achat immobilier</option>
                      <option>Études des enfants</option>
                      <option>Constitution d'épargne</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Montant visé (€):</label>
                    <input type="number" placeholder="500000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Horizon (années):</label>
                    <input type="number" placeholder="20" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Capacité d'épargne mensuelle (€):</label>
                    <input type="number" placeholder="800" className="bilan-form-select" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section Synthèse & Commentaires */}
          {activeTab === 'synthese-commentaires' && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr', 
              gap: '1.5rem', 
              marginBottom: '2rem' 
            }}>
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Synthèse Patrimoniale
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Situation actuelle:</label>
                    <textarea 
                      placeholder="Décrivez votre situation patrimoniale actuelle..." 
                      rows="3"
                      className="bilan-form-textarea" 
                    />
                  </div>
                  <div>
                    <label className="bilan-form-label">Projets à court terme:</label>
                    <textarea 
                      placeholder="Vos projets dans les 2-3 prochaines années..." 
                      rows="3"
                      className="bilan-form-textarea" 
                    />
                  </div>
                  <div>
                    <label className="bilan-form-label">Commentaires libres:</label>
                    <textarea 
                      placeholder="Informations complémentaires, questions, précisions..." 
                      rows="4"
                      className="bilan-form-textarea" 
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section Optimisation Fiscale */}
          {activeTab === 'optimisation-fiscale' && (
            <div className="bilan-tax-grid">
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Situation Fiscale
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label className="bilan-form-label">Tranche marginale d'imposition (%):</label>
                    <select className="bilan-form-select">
                      <option>0% (non imposable)</option>
                      <option>11%</option>
                      <option>30%</option>
                      <option>41%</option>
                      <option>45%</option>
                    </select>
                  </div>
                  <div>
                    <label className="bilan-form-label">Impôts sur le revenu (€/an):</label>
                    <input type="number" placeholder="8000" className="bilan-form-input" />
                  </div>
                  <div>
                    <label className="bilan-form-label">Taxe foncière (€/an):</label>
                    <input type="number" placeholder="1200" className="bilan-form-select" />
                  </div>
                  <div>
                    <label className="bilan-checkbox-container">
                      <input type="checkbox" className="bilan-checkbox" />
                      <span className="bilan-checkbox-label">Assujetti à l'IFI</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="bilan-pro-card">
                <h3 className="bilan-pro-title">
                  Dispositifs Utilisés
                </h3>
                <div className="bilan-form-container">
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" />
                      <span><strong>PEA</strong></span>
                    </label>
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" />
                      <span><strong>Assurance vie</strong></span>
                    </label>
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" />
                      <span><strong>PERP/PER</strong></span>
                    </label>
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" />
                      <span><strong>Investissement locatif (Pinel, etc.)</strong></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section Mon Bilan PDF */}
          {activeTab === 'bilan-pdf' && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr', 
              gap: '1.5rem', 
              marginBottom: '2rem' 
            }}>
              {/* Paramètres de simulation */}
              <div className="bilan-content-card-compact">
                <h3 className="bilan-section-title">⚙️ Paramètres de simulation</h3>
                
                <div className="bilan-investment-section">
                  <h4 className="bilan-investment-title">Type d'investissement</h4>
                  <div className="bilan-investment-grid">
                    <div 
                      className={`bilan-investment-option ${simulationParams.investmentType === 'monthly' ? 'active' : ''}`}
                      onClick={() => setSimulationParams({...simulationParams, investmentType: 'monthly'})}
                    >
                      <div className="bilan-investment-option-title">Versements réguliers</div>
                      <div className="bilan-investment-option-desc">Épargne mensuelle classique</div>
                    </div>
                    <div 
                      className={`bilan-investment-option ${simulationParams.investmentType === 'lumpsum' ? 'active' : ''}`}
                      onClick={() => setSimulationParams({...simulationParams, investmentType: 'lumpsum'})}
                    >
                      <div className="bilan-investment-option-title">Montant unique</div>
                      <div className="bilan-investment-option-desc">Placement d'un capital</div>
                    </div>
                    <div 
                      className={`bilan-investment-option ${simulationParams.investmentType === 'mixed' ? 'active' : ''}`}
                      onClick={() => setSimulationParams({...simulationParams, investmentType: 'mixed'})}
                    >
                      <div className="bilan-investment-option-title">Capital + Épargne</div>
                      <div className="bilan-investment-option-desc">Les deux combinés</div>
                    </div>
                  </div>
                </div>

                {/* Sliders */}
                {(simulationParams.investmentType === 'lumpsum' || simulationParams.investmentType === 'mixed') && (
                  <div style={{ marginBottom: '2rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                      Capital initial: {simulationParams.lumpSum.toLocaleString()}€
                    </label>
                    <input 
                      type="range" 
                      min="1000" 
                      max="100000" 
                      step="1000"
                      value={simulationParams.lumpSum} 
                      onChange={(e) => setSimulationParams({...simulationParams, lumpSum: parseInt(e.target.value)})}
                      className="bilan-form-range"
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      <span>1 000€</span>
                      <span>50 000€</span>
                      <span>100 000€</span>
                    </div>
                  </div>
                )}

                {(simulationParams.investmentType === 'monthly' || simulationParams.investmentType === 'mixed') && (
                  <div style={{ marginBottom: '2rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                      Versement mensuel: {simulationParams.monthlyAmount}€
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="2000"
                      step="50"
                      value={simulationParams.monthlyAmount}
                      onChange={(e) => setSimulationParams({...simulationParams, monthlyAmount: parseInt(e.target.value)})}
                      style={{
                        width: '100%',
                        height: '8px',
                        borderRadius: '4px',
                        background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${(simulationParams.monthlyAmount - 50) / 19.5}%, #e5e7eb ${(simulationParams.monthlyAmount - 50) / 19.5}%, #e5e7eb 100%)`,
                        outline: 'none',
                        appearance: 'none',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      <span>50€</span>
                      <span>1 000€</span>
                      <span>2 000€</span>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    Durée de placement: {simulationParams.duration} ans
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={simulationParams.duration}
                    onChange={(e) => setSimulationParams({...simulationParams, duration: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      height: '8px',
                      borderRadius: '4px',
                      background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${(simulationParams.duration - 1) / 29 * 100}%, #e5e7eb ${(simulationParams.duration - 1) / 29 * 100}%, #e5e7eb 100%)`,
                      outline: 'none',
                      appearance: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    <span>1 an</span>
                    <span>15 ans</span>
                    <span>30 ans</span>
                  </div>
                </div>

                {simulationParams.investmentType === 'mixed' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="bilan-checkbox-container">
                      <input
                        type="checkbox"
                        checked={simulationParams.withRegularPayments}
                        onChange={(e) => setSimulationParams({...simulationParams, withRegularPayments: e.target.checked})}
                        className="bilan-checkbox"
                      />
                      <span className="bilan-checkbox-label">Ajouter des versements mensuels de {simulationParams.monthlyAmount}€</span>
                    </label>
                    <div className="bilan-checkbox-description">
                      Combinez un capital initial avec une épargne régulière pour maximiser votre potentiel
                    </div>
                  </div>
                )}

                {/* Résumé de stratégie */}
                <div className="bilan-strategy-summary">
                  <h4 className="bilan-strategy-title">
                    Résumé de votre stratégie
                  </h4>
                  <div className="bilan-strategy-grid">
                    {(simulationParams.investmentType === 'lumpsum' || simulationParams.investmentType === 'mixed') && (
                      <div className="bilan-strategy-item">
                        <div className="bilan-strategy-value">
                          {simulationParams.lumpSum.toLocaleString()}€
                        </div>
                        <div className="bilan-strategy-label">Capital initial</div>
                      </div>
                    )}
                    {(simulationParams.investmentType === 'monthly' || (simulationParams.investmentType === 'mixed' && simulationParams.withRegularPayments)) && (
                      <div className="bilan-strategy-item">
                        <div className="bilan-strategy-value">
                          {simulationParams.monthlyAmount}€
                        </div>
                        <div className="bilan-strategy-label">Mensuel</div>
                      </div>
                    )}
                    <div className="bilan-strategy-item">
                      <div className="bilan-strategy-value">
                        {simulationParams.duration} ans
                      </div>
                      <div className="bilan-strategy-label">Durée</div>
                    </div>
                    <div className="bilan-strategy-item">
                      <div className="bilan-strategy-value">
                        {totalInvested.toLocaleString()}€
                      </div>
                      <div className="bilan-strategy-label">Total investi</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bilan-content-card">
                <h3 style={{ color: '#374151', marginBottom: '1rem', fontSize: '1.5rem', fontWeight: '700' }}>📊 Résumé de votre simulation</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                      {finalValue.toLocaleString()}€
                    </div>
                    <div style={{ color: '#6b7280' }}>Valeur finale</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
                      {totalInvested.toLocaleString()}€
                    </div>
                    <div style={{ color: '#6b7280' }}>Total investi</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                      +{totalGains.toLocaleString()}€
                    </div>
                    <div style={{ color: '#6b7280' }}>Gains totaux</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                      {simulationParams.duration} ans
                    </div>
                    <div style={{ color: '#6b7280' }}>Durée</div>
                  </div>
                </div>
              </div>

              {/* Évolution du patrimoine */}
              <div className="bilan-chart-card">
                <h3 className="bilan-chart-title">📈 Évolution projetée du patrimoine</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={projections}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="year" 
                      stroke="#666"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#666"
                      fontSize={12}
                      tickFormatter={(value) => `${Math.round(value/1000)}k€`}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${Math.round(value).toLocaleString()}€`, name]}
                      labelFormatter={(label) => `Année ${label}`}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="invested" 
                      stroke="var(--blue-400)" 
                      strokeWidth={3}
                      dot={{ fill: 'var(--blue-400)', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: 'var(--blue-400)', strokeWidth: 2, fill: '#fff' }}
                      name="Capital investi"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="var(--teal-400)" 
                      strokeWidth={3}
                      dot={{ fill: 'var(--teal-400)', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: 'var(--teal-400)', strokeWidth: 2, fill: '#fff' }}
                      name="Valeur totale"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Allocation recommandée */}
              <div className="bilan-allocation-card">
                <div className="bilan-allocation-header">
                  <h3 className="bilan-allocation-title">Allocation recommandée</h3>
                  <div className="bilan-allocation-controls">
                    {!isEditingAllocation ? (
                      <button 
                        onClick={startEditingAllocation}
                        className="bilan-edit-allocation-btn"
                      >
                        ✏️ Personnaliser
                      </button>
                    ) : (
                      <div className="bilan-allocation-actions">
                        <button 
                          onClick={resetAllocation}
                          className="bilan-reset-allocation-btn"
                        >
                          🔄 Réinitialiser
                        </button>
                        <button 
                          onClick={() => setIsEditingAllocation(false)}
                          className="bilan-save-allocation-btn"
                        >
                          ✅ Valider
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {isEditingAllocation && (
                  <div className="bilan-allocation-editor">
                    <div className="bilan-allocation-sliders">
                      {Object.entries(optimalAllocation)
                        .filter(([_, weight]) => weight > 0)
                        .map(([product, weight]) => {
                          const productInfo = soeasi_products[product];
                          const IconComponent = productInfo?.icon || Shield;
                          return (
                            <div key={product} className="bilan-allocation-slider-item">
                              <div className="bilan-allocation-slider-header">
                                <div className="bilan-allocation-product-info">
                                  <IconComponent size={20} style={{ color: productInfo?.color }} />
                                  <span className="bilan-allocation-product-name">{productInfo?.name}</span>
                                </div>
                                <div className="bilan-allocation-percentage">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={Math.round(weight)}
                                    onChange={(e) => handleAllocationChange(product, parseInt(e.target.value) || 0)}
                                    className="bilan-allocation-input"
                                  />
                                  <span>%</span>
                                </div>
                              </div>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={weight}
                                onChange={(e) => handleAllocationChange(product, parseInt(e.target.value))}
                                className="bilan-allocation-slider"
                                style={{
                                  background: `linear-gradient(90deg, ${productInfo?.color} 0%, ${productInfo?.color}33 100%)`
                                }}
                              />
                            </div>
                          );
                        })}
                    </div>
                    <div className="bilan-allocation-total">
                      Total: {Math.round(Object.values(optimalAllocation).reduce((sum, val) => sum + val, 0))}%
                      {Math.round(Object.values(optimalAllocation).reduce((sum, val) => sum + val, 0)) !== 100 && (
                        <span className="bilan-allocation-warning">⚠️ Le total doit être 100%</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="bilan-allocation-content">
                  <div className="bilan-allocation-chart">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={Object.entries(optimalAllocation)
                            .filter(([_, weight]) => weight > 0)
                            .map(([product, weight]) => ({
                              name: soeasi_products[product]?.name || product,
                              value: weight,
                              color: soeasi_products[product]?.color || '#3b82f6'
                            }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                        >
                          {Object.entries(optimalAllocation)
                            .filter(([_, weight]) => weight > 0)
                            .map(([product, _], index) => (
                              <Cell key={`cell-${index}`} fill={soeasi_products[product]?.color || '#3b82f6'} />
                            ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            color: '#374151'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="bilan-allocation-products">
                    {Object.entries(optimalAllocation)
                      .filter(([_, weight]) => weight > 0)
                      .map(([product, weight]) => {
                        const productInfo = soeasi_products[product];
                        const IconComponent = productInfo?.icon || Shield;
                        return (
                          <div key={product} className="bilan-allocation-product-item" style={{
                            borderColor: `${productInfo?.color}33`,
                            background: `linear-gradient(135deg, white 0%, ${productInfo?.color}08 100%)`
                          }}>
                            <div className="bilan-allocation-product-icon" style={{ 
                              background: `${productInfo?.color}15`,
                              color: productInfo?.color 
                            }}>
                              <IconComponent size={18} />
                            </div>
                            <div className="bilan-allocation-product-details">
                              <div className="bilan-allocation-product-name">{productInfo?.name}</div>
                              <div className="bilan-allocation-product-percentage" style={{ color: productInfo?.color }}>
                                {Math.round(weight)}%
                              </div>
                              <div className="bilan-allocation-product-bar">
                                <div 
                                  className="bilan-allocation-product-bar-fill" 
                                  style={{ 
                                    width: `${weight}%`,
                                    background: `linear-gradient(90deg, ${productInfo?.color}, ${productInfo?.color}80)`
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Détails des produits */}
                <div className="bilan-products-card">
                  <h3 className="bilan-products-title">💼 Détails des produits sélectionnés</h3>
                  <div className="bilan-products-grid">
                    {Object.entries(optimalAllocation)
                    .filter(([_, weight]) => weight > 0)
                    .map(([product, weight]) => {
                      const productInfo = soeasi_products[product];
                      const IconComponent = productInfo?.icon || Shield;
                      return (
                        <div key={product} className="bilan-product-detail-item" style={{
                          borderColor: `${productInfo?.color}33`
                        }}>
                          <div className="bilan-product-detail-icon">
                            <IconComponent size={24} style={{ color: productInfo?.color }} />
                          </div>
                          <div className="bilan-product-detail-content">
                            <div className="bilan-product-detail-name">{productInfo?.name}</div>
                            <div className="bilan-product-detail-percentage" style={{ color: productInfo?.color }}>
                              {Math.round(weight)}%
                            </div>
                            <div className="bilan-product-detail-info">
                              <span>📈 Rendement: {productInfo?.return}%</span>
                              <span>💰 Frais: {productInfo?.fees}%</span>
                              <span>⚠️ Risque: {productInfo?.risk}/10</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="bilan-actions-container">
                  <button 
                    className="bilan-btn-primary"
                    onClick={downloadPDF}
                  >
                    <Download size={20} />
                    Télécharger le PDF
                  </button>
                  <button 
                    className="bilan-btn-secondary"
                    onClick={() => onNavigateToSOeasi()}
                  >
                    <Calculator size={20} />
                    Modifier la simulation
                  </button>
                  {simulationSaved && (
                    <div className="bilan-success-message">
                      <CheckCircle size={20} />
                      Simulation sauvegardée
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BilanPatrimonial;
